<template>
  <div class="sales_return__report">
    <ReportFormat :records="reports_data" @show_result="show_result" @download_excel_report="exportExcel" :isLoading="loading">
      <template #report-form>
        <div class="vx-row pt-5 px-5">
          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>From Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="fromDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report From Date" />
                <span class="text-danger text-sm" v-show="errors.has('Report From Date')">{{ errors.first('Report From Date') }}</span>
              </div>
            </div>

            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>Category Name</h6>
              </div>
              <div class="vx-col w-3/5">
                <select-2
                  class="w-full"
                  autocomplete
                  :value="category"
                  @input="(item) => (category = item.value)"
                  placeholder="Please Select Category"
                  name="Category"
                  label="category_name"
                  val="id"
                  :extra="[{ label: 'All', value: null }]"
                  :ssr="true"
                  action="expense_category/getExpenseCategory"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Category')">{{ errors.first('Category') }}</span>
              </div>
            </div>
          </div>

          <div class="vx-col w-full lg:w-5/12 md:w-1/2">
            <div class="vx-row mb-2 items-center">
              <div class="vx-col w-2/5 text-right">
                <h6>To Date</h6>
              </div>
              <div class="vx-col w-3/5 relative">
                <date-picker :appendToBody="false" v-model="toDate" placeholder="MM-DD-YYYY" format="MM-DD-YYYY" valueType="format" class="w-full" v-validate="'required'" name="Report To Date" />
                <span class="text-danger text-sm" v-show="errors.has('Report To Date')">{{ errors.first('Report To Date') }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #record-table>
        <template>
          <vs-th>#</vs-th>
          <vs-th>Expense Code</vs-th>
          <vs-th>Expense Date</vs-th>
          <vs-th>Category Name</vs-th>
          <vs-th>Reference No.</vs-th>
          <vs-th>Expense for</vs-th>
          <vs-th>Amount</vs-th>
          <vs-th>Note</vs-th>
          <vs-th>Created by</vs-th>
        </template>

        <template ref="tableBody">
          <vs-tr :data="tr" :key="i" v-for="(tr, i) in reports_data">
            <vs-td>{{ i + 1 }}</vs-td>
            <vs-td>{{ `EX${tr.id}` }}</vs-td>
            <vs-td>{{ formatDate(tr.expenseDate) }}</vs-td>
            <vs-td>{{ tr.expenseCategory.category_name }}</vs-td>
            <vs-td>{{ tr.referenceNo }}</vs-td>
            <vs-td>{{ tr.expenseFor }}</vs-td>
            <vs-td>$ {{ tr.expenseAmount | currencyFilter }}</vs-td>
            <vs-td>{{ tr.note }}</vs-td>
            <vs-td>{{ tr.user.username }}</vs-td>
          </vs-tr>

          <vs-tr v-if="reports_data.length">
            <vs-td colspan="6" class="text-right font-bold">Total :</vs-td>
            <vs-td class="font-bold">$ {{ calculate_total_expanse | currencyFilter }}</vs-td>
            <vs-td colspan="2"></vs-td>
          </vs-tr>
        </template>
      </template>
    </ReportFormat>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import moment from 'moment'
import { mapState, mapMutations } from 'vuex'
import ReportFormat from '@/views/reports/components/ReportFormat.vue'
import { dateMixins } from '@/mixins/dateMixins.js'
import { paymentMixins } from '@/utils/paymentTypes'
import Select2 from '@/components/custom/form-elements/Select2.vue'

export default {
  name: 'SalesReturnReport',

  components: {
    DatePicker,
    ReportFormat,
    Select2
  },

  data() {
    return {
      page: 1,
      limit: 0,
      search: '',
      date: 'MM-dd-yyyy',
      fromDate: moment().format('MM-DD-YYYY'),
      category: null,
      toDate: moment().format('MM-DD-YYYY'),
      loading: false
    }
  },

  mixins: [paymentMixins, dateMixins],

  methods: {
    moment,
    ...mapMutations('report', { set_state: 'SET_STATE' }),
    ...mapMutations('expense_category', { set_expense_category_state: 'SET_STATE' }),

    async show_result() {
      this.loading = true
      const payload = {
        page: this.page,
        limit: this.limit,
        search: this.search,
        categoryId: this.category || null,
        fromDate: moment(this.fromDate).format('YYYY-MM-DD'),
        toDate: moment(this.toDate).format('YYYY-MM-DD')
      }

      this.$store
        .dispatch('report/getExpenseReport', payload)
        .then(() => {
          this.loading = false
        })
        .catch((err) => {
          const { message } = err
          this.loading = false
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    async exportExcel() {
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(
        this.reports_data.map((item, index) => ({
          '#': index + 1,
          'Expense Code': `EX${item.id}`,
          'Expense Date': dateMixins.methods.formatDate(item.expenseDate),
          'Category Name': item.expenseCategory.category_name,
          'Reference No.': item.referenceNo,
          'Expense for': item.expenseFor,
          Amount: paymentMixins.methods.mathRound(item.expenseAmount, 2),
          Note: item.note,
          'Created by': item.user.username
        }))
      )
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1')
      XLSX.writeFile(wb, 'Expense Report.xlsx')
    }
  },

  computed: {
    ...mapState('report', ['reports_data', 'total', 'FilteredCount']),

    calculate_total_expanse() {
      return this.reports_data.reduce((acc, item) => {
        return acc + item.expenseAmount
      }, 0)
    }
  },

  filters: {
    // currencyFilter rounded to 2 point
    currencyFilter(value) {
      return paymentMixins.methods.mathRound(Number(value), 2)
    }
  },

  async beforeDestroy() {
    await this.set_state({ action: 'reports_data', data: [] })
    await this.set_expense_category_state({ action: 'expense_category', data: [] })
  }
}
</script>

<style></style>
